import React, { useState } from 'react';
import { Recipe } from '../types';
import { isStandaloneApp, isIOSDevice, savePendingSharedRecipe, prepareOpenInApp, recipeTokenToShareUrl } from '../services/storageService';

interface SharedRecipeScreenProps {
  recipe: Recipe;
  /** Firestore share id, of 'data:<encoded>' voor een link met receptdata */
  shareToken: string | null;
  onImport: () => void;
  onCancel: () => void;
  isImporting: boolean;
}

export const SharedRecipeScreen: React.FC<SharedRecipeScreenProps> = ({ recipe, shareToken, onImport, onCancel, isImporting }) => {
  const [showIOSTip, setShowIOSTip] = useState(false);
  const isInstalledPWA = isStandaloneApp();

  const handleOpenInApp = async () => {
    if (!shareToken) return;
    savePendingSharedRecipe(shareToken);
    await prepareOpenInApp(recipeTokenToShareUrl(shareToken));
    setShowIOSTip(true);
  };

  return (
    <div className="relative flex flex-col h-screen w-full bg-background-light dark:bg-background-dark overflow-y-auto no-scrollbar pb-32">
        {/* Header Photo */}
        <div className="relative h-[360px] w-full shrink-0 overflow-hidden">
          <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between p-4 pt-8">
             <div className="bg-black/30 backdrop-blur-md text-white px-3 py-1 rounded-full text-xs font-bold border border-white/20 flex items-center gap-1.5">
                {isInstalledPWA ? (
                  <>
                    <span className="material-symbols-outlined text-primary text-[14px]">verified</span>
                    <span>superMarct App</span>
                  </>
                ) : (
                  <span>Gedeeld recept</span>
                )}
             </div>
             <button onClick={onCancel} className="size-10 flex items-center justify-center rounded-full bg-black/20 text-white backdrop-blur-md hover:bg-black/30 transition">
              <span className="material-symbols-outlined">close</span>
             </button>
          </div>
          <img src={recipe.image} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />
          <div className="absolute bottom-0 p-6 w-full z-20">
            {recipe.originalAuthor && (
                <div className="flex items-center gap-2 mb-2">
                    <span className="bg-primary text-black text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">Auteur</span>
                    <span className="text-white text-sm font-medium">{recipe.originalAuthor}</span>
                </div>
            )}
            <h1 className="text-3xl font-bold text-white leading-tight">{recipe.title}</h1>
            <div className="flex items-center gap-4 mt-2 text-gray-300 text-sm">
                <span className="flex items-center gap-1"><span className="material-symbols-outlined text-[16px]">schedule</span>{recipe.time}</span>
                <span className="flex items-center gap-1"><span className="material-symbols-outlined text-[16px]">restaurant</span>{recipe.category}</span>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6 flex-1">
            {/* Deep link switch banner for web browser users */}
            {!isInstalledPWA && (
              <div className="p-3.5 rounded-2xl bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-950/40 dark:to-green-950/30 border border-primary/30 text-xs">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1.5 font-extrabold text-gray-900 dark:text-white">
                    <span className="material-symbols-outlined text-primary text-[18px]">smartphone</span>
                    <span>App al geïnstalleerd?</span>
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-primary text-black px-2 py-0.5 rounded-full">Direct</span>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-[11px] leading-relaxed mb-2.5">
                  Open dit recept direct in de superMarct app op je apparaat.
                </p>
                <button
                  type="button"
                  onClick={handleOpenInApp}
                  className="w-full py-2.5 rounded-xl bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-bold text-xs flex items-center justify-center gap-2 hover:opacity-95 active:scale-[0.98] transition cursor-pointer shadow-sm"
                >
                  <span className="material-symbols-outlined text-[16px] text-primary">open_in_new</span>
                  <span>Openen in superMarct app</span>
                </button>
                {showIOSTip && (
                  <p className="text-[11px] text-emerald-800 dark:text-emerald-300 mt-2 bg-emerald-100/70 dark:bg-emerald-900/40 p-2 rounded-xl">
                    {isIOSDevice() ? (
                      <>✓ Link gekopieerd! Open <strong>superMarct</strong> op je beginscherm en tik op <strong>⋮ → Gedeelde link openen</strong>.</>
                    ) : (
                      <>✓ Recept klaargezet! Open <strong>superMarct</strong> op je beginscherm, het recept verschijnt direct.</>
                    )}
                  </p>
                )}
              </div>
            )}

            {/* Ingredients */}
            <div>
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <span className="material-symbols-outlined text-primary">grocery</span>
                    Ingrediënten
                </h2>
                <div className="space-y-3">
                    {recipe.ingredients.map((ing, idx) => (
                        <div key={idx} className="p-3 rounded-xl flex items-center justify-between bg-surface-light dark:bg-surface-dark border border-gray-100 dark:border-white/5">
                            <span className="font-medium text-gray-700 dark:text-gray-200">{ing.name}</span>
                            <span className="font-bold text-primary">{ing.amount}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Steps */}
            <div>
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <span className="material-symbols-outlined text-primary">cooking</span>
                    Bereiding
                </h2>
                <div className="space-y-6">
                    {recipe.steps.map((s, idx) => (
                        <div key={idx} className="flex gap-4">
                             <div className="flex flex-col items-center gap-2">
                                  <div className="size-8 shrink-0 bg-primary/20 text-primary rounded-full flex items-center justify-center font-bold text-sm">{idx+1}</div>
                                  {idx < recipe.steps.length - 1 && <div className="w-0.5 flex-1 bg-gray-100 dark:bg-white/10 min-h-[20px]"></div>}
                              </div>
                              <div className="flex-1 pb-4">
                                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed pt-1">{s}</p>
                              </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>

        {/* Sticky Bottom Action */}
        <div className="fixed bottom-0 left-0 w-full p-4 bg-white/80 dark:bg-background-dark/80 backdrop-blur-lg border-t border-gray-100 dark:border-white/5 z-50">
            <button 
                onClick={onImport}
                disabled={isImporting}
                className="w-full py-4 bg-primary text-black font-bold rounded-2xl shadow-lg shadow-primary/20 active:scale-95 transition-all flex items-center justify-center gap-2 text-lg disabled:opacity-50"
            >
                {isImporting ? (
                    <span className="material-symbols-outlined animate-spin">sync</span>
                ) : (
                    <span className="material-symbols-outlined filled">add_circle</span>
                )}
                {isImporting ? 'Toevoegen...' : 'Toevoegen aan Mijn superMarct'}
            </button>
        </div>
    </div>
  );
};
